import * as React from "react"
import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { NeedHelpButton } from "@/components/need-help-button"
import { PlusIcon, type PlusIconHandle } from "@/components/ui/plus-icon"
import {
  RefreshCCWIcon,
  type RefreshCCWIconHandle,
} from "@/components/ui/refresh-ccw-icon"
import { UploadIcon, type UploadIconHandle } from "@/components/ui/upload-icon"
import { cn } from "@/lib/utils"

const BROKER_MANAGEMENT_HELP_URL = "https://help.tesseratrades.com/brokers-and-data"

export type BrokerManagementHeaderActionsProps = {
  onConnectNewBroker: () => void
  connectDisabled?: boolean
}

export function BrokerManagementHeaderActions({
  onConnectNewBroker,
  connectDisabled = false,
}: BrokerManagementHeaderActionsProps): React.ReactElement {
  const plusIconRef = React.useRef<PlusIconHandle | null>(null)

  const startPlusAnimation = React.useCallback(() => {
    void plusIconRef.current?.startAnimation()
  }, [])

  const stopPlusAnimation = React.useCallback(() => {
    void plusIconRef.current?.stopAnimation()
  }, [])

  const plusHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startPlusAnimation,
      onMouseEnter: startPlusAnimation,
      onMouseLeave: stopPlusAnimation,
    }),
    [startPlusAnimation, stopPlusAnimation]
  )

  return (
    <div className="flex flex-nowrap items-center justify-end gap-2">
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="shrink-0"
        disabled={connectDisabled}
        onClick={onConnectNewBroker}
        {...plusHoverHandlers}
      >
        <PlusIcon
          ref={plusIconRef}
          data-icon="inline-start"
          size={16}
          className="shrink-0 text-current"
          aria-hidden
        />
        Connect new broker
      </Button>
      <NeedHelpButton href={BROKER_MANAGEMENT_HELP_URL} />
    </div>
  )
}

export type BrokerImportTradesButtonProps = {
  onImportTrades: () => void
  disabled?: boolean
}

export function BrokerImportTradesButton({
  onImportTrades,
  disabled = false,
}: BrokerImportTradesButtonProps): React.ReactElement {
  const uploadIconRef = React.useRef<UploadIconHandle | null>(null)

  const startUploadAnimation = React.useCallback(() => {
    void uploadIconRef.current?.startAnimation()
  }, [])

  const stopUploadAnimation = React.useCallback(() => {
    void uploadIconRef.current?.stopAnimation()
  }, [])

  const uploadHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startUploadAnimation,
      onMouseEnter: startUploadAnimation,
      onMouseLeave: stopUploadAnimation,
    }),
    [startUploadAnimation, stopUploadAnimation]
  )

  return (
    <Button
      type="button"
      variant="outline-brand"
      size="sm"
      data-font="primary"
      data-broker-import-trades=""
      className="shrink-0"
      disabled={disabled}
      onClick={onImportTrades}
      {...uploadHoverHandlers}
    >
      <UploadIcon
        ref={uploadIconRef}
        data-icon="inline-start"
        size={16}
        className="shrink-0 text-current"
        aria-hidden
      />
      Import trades
    </Button>
  )
}

export type BrokerResyncTradesButtonProps = {
  onResyncTrades: () => void
  disabled?: boolean
  isLoading?: boolean
}

export function BrokerResyncTradesButton({
  onResyncTrades,
  disabled = false,
  isLoading = false,
}: BrokerResyncTradesButtonProps): React.ReactElement {
  const refreshIconRef = React.useRef<RefreshCCWIconHandle | null>(null)

  const startRefreshAnimation = React.useCallback(() => {
    if (disabled || isLoading) return
    void refreshIconRef.current?.startAnimation()
  }, [disabled, isLoading])

  const stopRefreshAnimation = React.useCallback(() => {
    void refreshIconRef.current?.stopAnimation()
  }, [])

  const refreshHoverHandlers = React.useMemo(
    () =>
      disabled || isLoading
        ? {}
        : {
            onPointerDownCapture: startRefreshAnimation,
            onMouseEnter: startRefreshAnimation,
            onMouseLeave: stopRefreshAnimation,
          },
    [disabled, isLoading, startRefreshAnimation, stopRefreshAnimation]
  )

  return (
    <Button
      type="button"
      variant="gradient"
      size="sm"
      data-font="primary"
      data-broker-resync-trades=""
      className={cn("shrink-0", isLoading && "opacity-90")}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      onClick={onResyncTrades}
      {...refreshHoverHandlers}
    >
      {isLoading ? (
        <Loader2 data-icon="inline-start" className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        <RefreshCCWIcon
          ref={refreshIconRef}
          data-icon="inline-start"
          size={16}
          className="shrink-0 text-current"
          aria-hidden
        />
      )}
      {isLoading ? "Resyncing…" : "Resync trades"}
    </Button>
  )
}
